import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './MainApp.css';
import useReveal from './useReveal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

const PIPELINE = [
  { key: 'parse', label: 'Parsing your description', detail: 'char n-gram + fine-tuned encoder' },
  { key: 'match', label: 'Mapping to clinical markers', detail: '132 canonical symptoms' },
  { key: 'score', label: 'Scoring conditions', detail: 'profile + random forest ensemble' },
  { key: 'kb', label: 'Searching beyond the training set', detail: 'condition knowledge base' },
  { key: 'plan', label: 'Building your care plan', detail: '7-day diet and exercise' },
];

const EXAMPLES = [
  'I’ve had a throbbing headache for 3 days and bright light hurts my eyes',
  'Burning up with fever, throwing up since yesterday, no cough',
  'Loose motions and stomach cramps for a week, feeling very weak',
  'My eyes have gone yellow and my urine is dark',
];

/* ---------------- icons ---------------- */
const I = {
  back: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M19 12H5M11 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  spark: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8" strokeLinecap="round"/></svg>,
  pill: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}><rect x="2.5" y="8.5" width="19" height="7" rx="3.5" transform="rotate(-45 12 12)"/><path d="M8.5 8.5 15.5 15.5"/></svg>,
  shield: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}><path d="M12 3l7 3v6c0 4.4-3 7.9-7 9-4-1.1-7-4.6-7-9V6l7-3Z" strokeLinejoin="round"/><path d="m9 12 2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  store: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}><path d="M4 9h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9Z" strokeLinejoin="round"/><path d="M4 9 6 4h12l2 5" strokeLinejoin="round"/><path d="M12 13v4M10 15h4" strokeLinecap="round"/></svg>,
  cal: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...p}><rect x="3" y="5" width="18" height="16" rx="2.5"/><path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round"/></svg>,
  x: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" {...p}><path d="M6 6l12 12M18 6 6 18" strokeLinecap="round"/></svg>,
  alert: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" {...p}><path d="M12 8v5M12 16.5v.5" strokeLinecap="round"/><path d="M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" strokeLinejoin="round"/></svg>,
  chip: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...p}><rect x="7" y="7" width="10" height="10" rx="2"/><path d="M10 3v4M14 3v4M10 17v4M14 17v4M3 10h4M3 14h4M17 10h4M17 14h4" strokeLinecap="round"/></svg>,
};

const TRIAGE = {
  emergency: { tone: 'danger', tag: 'Emergency' },
  urgent: { tone: 'warn', tag: 'Urgent' },
  uncertain: { tone: 'muted', tag: 'Uncertain' },
  routine: { tone: 'ok', tag: 'Routine' },
};

/* ============================================================ */

function Loader() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => Math.min(s + 1, PIPELINE.length - 1)), 1400);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="panel loader">
      <div className="loader-head">
        <span className="scan"><span /></span>
        <div>
          <h3>Analysing</h3>
          <p>Running locally — no data leaves the server.</p>
        </div>
      </div>
      <ol className="pipeline">
        {PIPELINE.map((p, i) => (
          <li key={p.key} className={i < step ? 'done' : i === step ? 'active' : ''}>
            <span className="dot" />
            <span className="pl-label">{p.label}</span>
            <code>{p.detail}</code>
          </li>
        ))}
      </ol>
      <small>First run can take ~15s while the server wakes up.</small>
    </div>
  );
}

function Bar({ value, tone = 'c1' }) {
  return (
    <div className={`bar tone-${tone}`}>
      <span style={{ width: `${Math.max(2, Math.round(value * 100))}%` }} />
    </div>
  );
}

function DayPlan({ diet, workout, links }) {
  const [day, setDay] = useState(1);
  const days = Object.keys(diet || {}).map(Number).filter((n) => !isNaN(n)).sort((a, b) => a - b);
  if (!days.length) return <p className="empty">No plan was generated.</p>;
  const d = diet[day] || diet[String(day)] || {};
  const w = workout?.[day] ?? workout?.[String(day)] ?? '';
  const meals = [
    { k: 'Breakfast', v: d.breakfast, t: '08:00', tone: 'c1' },
    { k: 'Lunch', v: d.lunch, t: '13:00', tone: 'c2' },
    { k: 'Dinner', v: d.dinner, t: '20:00', tone: 'c3' },
  ];
  return (
    <div className="dayplan">
      <div className="day-tabs">
        {days.map((n) => (
          <button key={n} className={`day-tab ${n === day ? 'on' : ''}`} onClick={() => setDay(n)}>
            <span className="day-tab-l">Day</span><span className="day-tab-n">{n}</span>
          </button>
        ))}
      </div>
      <div className="day-body" key={day}>
        <div className="meals">
          {meals.map((m, i) => (
            <div className={`meal tone-${m.tone}`} key={m.k} style={{ '--i': i }}>
              <div className="meal-head"><span className="meal-name">{m.k}</span><span className="meal-time">{m.t}</span></div>
              <p>{m.v || 'Balanced meal'}</p>
            </div>
          ))}
        </div>
        {d.snack && (
          <div className="meal snack" style={{ '--i': 3 }}>
            <div className="meal-head"><span className="meal-name">Snack</span><span className="meal-time">17:00</span></div>
            <p>{d.snack}</p>
          </div>
        )}
        <div className="workout-card" style={{ '--i': 4 }}>
          <div className="meal-head"><span className="meal-name">Morning workout</span><span className="meal-time">07:00</span></div>
          <p>{w || 'Light morning exercise'}</p>
        </div>
        {d.hydration && <p className="hydration">💧 {d.hydration}</p>}
        <div className="day-actions" style={{ '--i': 5 }}>
          {links?.[(day - 1) * 2] && <a className="cal-btn" href={links[(day - 1) * 2]} target="_blank" rel="noopener noreferrer"><I.cal width="15" height="15"/> Add Day {day} meals</a>}
          {links?.[(day - 1) * 2 + 1] && <a className="cal-btn" href={links[(day - 1) * 2 + 1]} target="_blank" rel="noopener noreferrer"><I.cal width="15" height="15"/> Add Day {day} workout</a>}
        </div>
      </div>
    </div>
  );
}

function Results({ data, onDropSymptom }) {
  useReveal([data]);
  const t = TRIAGE[data.triage?.level] || TRIAGE.routine;
  const understood = data.understood?.matched || [];

  // When the classifier is unsure, or something time-critical was retrieved,
  // a 29%-confidence guess must not be the biggest thing on the page. The
  // knowledge-base card moves above it and the headline is played down.
  const demote = data.low_confidence || data.triage?.level === 'emergency';

  const assessment = (
    <section className={`panel reveal ${demote ? 'demoted' : ''}`} style={{ '--delay': '80ms' }}>
      <header className="panel-head">
        <span className="panel-icon tone-c2"><I.spark width="18" height="18" /></span>
        <div>
          <h3>Closest match in the trained set</h3>
          <p className="sub">From the {data.meta?.known_conditions} conditions the model was trained on.</p>
        </div>
        {data.low_confidence && <span className="lowconf">low confidence</span>}
      </header>
      <h2 className="dx gradient-text">{data.disease}</h2>
      {data.low_confidence && (
        <p className="caveat">
          The top candidates score close together, so treat this as a shortlist rather than an
          answer. Add more detail about how you feel to sharpen it.
        </p>
      )}
      <p className="dx-desc">{data.description}</p>
      <div className="diffs">
        {data.differentials?.map((d, i) => (
          <div className="diff" key={d.condition} style={{ '--i': i }}>
            <div className="diff-top"><span>{d.condition}</span><b>{Math.round((d.confidence || 0) * 100)}%</b></div>
            <Bar value={d.confidence || 0} tone={i === 0 ? 'c1' : 'c2'} />
          </div>
        ))}
      </div>
      <p className="severity-line"><b>{data.category}</b> · severity {data.severity?.score}/100 ({data.severity?.band})</p>
    </section>
  );

  const knowledgeBase = data.other_possibilities?.length > 0 && (
    <section className="panel reveal" style={{ '--delay': '120ms' }}>
      <header className="panel-head">
        <span className="panel-icon tone-c3"><I.alert width="18" height="18" /></span>
        <div>
          <h3>{demote ? 'Worth ruling out first' : 'Outside the trained set'}</h3>
          <p className="sub">Retrieved from a {data.meta?.kb_conditions}-condition knowledge base the classifier cannot reach. Suggestions, not predictions.</p>
        </div>
      </header>
      <div className="kb-grid">
        {data.other_possibilities.map((s) => (
          <div className={`kb u-${s.urgency}`} key={s.name}>
            <div className="kb-top"><strong>{s.name}</strong><span className="u-tag">{s.urgency}</span></div>
            <p>{s.description}</p>
            <p className="kb-advice">{s.advice}</p>
            <span className="kb-sim">match {Math.round(s.similarity * 100)}%</span>
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <div className="results">
      {/* triage */}
      <div className={`triage reveal tone-${t.tone}`}>
        <span className="triage-icon"><I.alert width="20" height="20" /></span>
        <div>
          <span className="triage-tag">{t.tag}</span>
          <h3>{data.triage?.headline}</h3>
          {data.red_flags?.length > 0 && (
            <ul className="flags">{data.red_flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
          )}
          {data.triage?.reason && <p className="triage-reason">{data.triage.reason}</p>}
        </div>
      </div>

      {/* what the model understood */}
      <section className="panel reveal" style={{ '--delay': '40ms' }}>
        <header className="panel-head">
          <span className="panel-icon tone-c1"><I.chip width="18" height="18" /></span>
          <div><h3>What we understood</h3><p className="sub">Remove anything we got wrong — the analysis updates.</p></div>
        </header>
        <div className="understood">
          {understood.map((m) => (
            <span className="ch" key={m.symptom}>
              {m.label}
              <em>{m.via === 'encoder' ? 'encoder' : 'lexical'} {Math.round(m.score * 100)}%</em>
              <button onClick={() => onDropSymptom(m.symptom)} aria-label={`Remove ${m.label}`}><I.x width="10" height="10" /></button>
            </span>
          ))}
          {understood.length === 0 && <p className="empty">No symptoms matched.</p>}
        </div>
        {data.understood?.negated?.length > 0 && (
          <p className="meta-line">Ruled out from your wording: <b>{data.understood.negated.join(', ')}</b></p>
        )}
        {data.understood?.unmatched?.length > 0 && (
          <p className="meta-line dim">Not recognised: {data.understood.unmatched.map((u, i) => <code key={i}>{u}</code>)}</p>
        )}
        {data.understood?.duration && (
          <p className="meta-line dim">Duration detected: {data.understood.duration.value} {data.understood.duration.unit}(s)</p>
        )}
      </section>

      {demote ? <>{knowledgeBase}{assessment}</> : <>{assessment}{knowledgeBase}</>}

      <div className="cols">
        <section className="panel reveal" style={{ '--delay': '60ms' }}>
          <header className="panel-head"><span className="panel-icon tone-c2"><I.pill width="18" height="18"/></span><h3>Medications</h3></header>
          <ul className="med-list">{data.medications?.map((m, i) => <li key={i} style={{ '--i': i }}><span className="dot" />{m}</li>)}</ul>
          <p className="note">Confirm any medication with a doctor or pharmacist first.</p>
        </section>
        <section className="panel reveal" style={{ '--delay': '100ms' }}>
          <header className="panel-head"><span className="panel-icon tone-c4"><I.shield width="18" height="18"/></span><h3>Precautions</h3></header>
          {data.precautions?.length
            ? <ol className="prec-list">{data.precautions.map((p, i) => <li key={i} style={{ '--i': i }}><span className="prec-n">{i + 1}</span>{p}</li>)}</ol>
            : <p className="empty">No precautions listed for this condition.</p>}
        </section>
      </div>

      <section className="panel reveal">
        <header className="panel-head">
          <span className="panel-icon tone-c1"><I.cal width="18" height="18"/></span>
          <div><h3>Your 7-day plan</h3><p className="sub">{data.pacing}</p></div>
        </header>
        <div className="plan-notes">
          <p><b>Focus on</b> {data.focus}</p>
          <p><b>Avoid</b> {data.avoid}</p>
        </div>
        <DayPlan diet={data.diet} workout={data.workout} links={data.calendar_links} />
      </section>

      <section className="panel reveal">
        <header className="panel-head">
          <span className="panel-icon tone-c3"><I.store width="18" height="18"/></span>
          <div><h3>Pharmacies near you</h3><p className="sub">Matched to your pincode.</p></div>
        </header>
        {data.pharmacies?.length
          ? <div className="pharm-grid">{data.pharmacies.map((ph, i) => (
              <a className="pharm" key={i} style={{ '--i': i }} target="_blank" rel="noopener noreferrer"
                 href={`https://www.google.com/maps/search/${encodeURIComponent(`${ph['Medical Store Name']} ${ph.Address}`)}`}>
                <span className="pharm-icon"><I.store width="16" height="16"/></span>
                <div><strong>{ph['Medical Store Name']}</strong><span>{ph.Address}</span></div>
              </a>))}</div>
          : <p className="empty">No pharmacies found for that pincode — try a nearby one.</p>}
      </section>

      {/* techy footer */}
      <div className="readout reveal">
        <span><i>nlu</i> {data.meta?.nlu_backend}</span>
        <span><i>scorer</i> {data.meta?.scorer}</span>
        <span><i>forest weight</i> {data.meta?.forest_weight}</span>
        <span><i>retrieval</i> {data.meta?.retrieval_backend}</span>
        <span><i>plan</i> {data.meta?.plan_source}</span>
      </div>

      <div className="disclaimer reveal">
        <strong>Medical disclaimer.</strong> This is an educational machine-learning project, not a
        medical device. It does not provide medical advice, diagnosis or treatment. Always consult a
        qualified healthcare professional. In an emergency, contact your local emergency services.
      </div>
    </div>
  );
}

/* ============================================================ */

function MainApp({ onBack }) {
  const [text, setText] = useState('');
  const [pincode, setPincode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef(null);

  const canSubmit = text.trim().length > 3 && !loading;

  const run = async (payload) => {
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await axios.post(`${API_URL}/analyze`, payload, { timeout: 90000 });
      setResult(res.data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    } catch (err) {
      const d = err.response?.data;
      if (d?.error === 'none_recognised') setError(d.message);
      else if (err.code === 'ECONNABORTED') setError('The server took too long — it may be waking up. Try again.');
      else if (err.response) setError(d?.error || `Server error (${err.response.status}).`);
      else setError('Could not reach the server. Check your connection.');
    } finally { setLoading(false); }
  };

  const submit = (e) => { e.preventDefault(); if (canSubmit) run({ text: text.trim(), pincode }); };

  const dropSymptom = (sym) => {
    if (!result) return;
    const keep = (result.understood?.matched || []).map((m) => m.symptom).filter((s) => s !== sym);
    if (!keep.length) { setError('Keep at least one symptom.'); return; }
    run({ text: '', symptoms: keep, pincode });
  };

  return (
    <div className="app">
      <div className="app-nav">
        <button className="back" onClick={onBack}><I.back width="16" height="16" /> Home</button>
        {result && <button className="back" onClick={() => { setResult(null); setText(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>New check</button>}
      </div>

      <header className="app-head">
        <span className="eyebrow">Symptom checker</span>
        <h1>Describe it <span className="gradient-text">in your own words</span></h1>
        <p>No dropdowns, no checkboxes. Write how you actually feel — the model maps your wording onto clinical markers.</p>
      </header>

      <form className="panel form" onSubmit={submit}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. I've had a throbbing headache for 3 days and bright light hurts my eyes…"
          rows={4}
          maxLength={2000}
        />
        <div className="examples">
          <span>Try:</span>
          {EXAMPLES.map((ex) => (
            <button type="button" key={ex} onClick={() => setText(ex)}>{ex.length > 46 ? ex.slice(0, 46) + '…' : ex}</button>
          ))}
        </div>
        <div className="row">
          <input className="text-input" value={pincode} inputMode="numeric" maxLength={6}
                 onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
                 placeholder="Pincode (optional) — for nearby pharmacies" />
          <button className="submit" type="submit" disabled={!canSubmit}>
            {loading ? <><span className="btn-spin" /> Analysing…</> : <><I.spark width="17" height="17" /> Analyse</>}
          </button>
        </div>
      </form>

      {error && <div className="error"><I.x width="15" height="15" />{error}</div>}

      <div ref={resultRef}>
        {loading && <Loader />}
        {result && !loading && <Results data={result} onDropSymptom={dropSymptom} />}
      </div>
    </div>
  );
}

export default MainApp;
